import { useEffect, useState } from "react";
import { TextField } from "./form";
import "./CommandPalette.scss";

type Command = {
  label: string;
  shortcut?: string;
  action: () => void;
};

const commands: Command[] = [
  { label: "Open dashboard", shortcut: "G D", action: () => navigate("/") },
  {
    label: "Open cameras",
    shortcut: "G C",
    action: () => navigate("/cameras"),
  },
  { label: "Open alerts", shortcut: "G A", action: () => navigate("/alerts") },
  { label: "Open events", shortcut: "G E", action: () => navigate("/events") },
  {
    label: "Open workflow",
    shortcut: "G W",
    action: () => navigate("/workflow"),
  },
];

function navigate(path: string) {
  window.location.href = path;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const filtered = commands.filter((command) =>
    command.label.toLowerCase().includes(query.toLowerCase()),
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
      if (
        event.ctrlKey &&
        event.shiftKey &&
        (event.code === "KeyP" || event.key.toLowerCase() === "p")
      ) {
        event.preventDefault();
        event.stopPropagation();
        setQuery("");
        setSelectedIndex(0);
        setOpen(true);
      }
      if (!open || !filtered.length) return;
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        setSelectedIndex((current) =>
          event.key === "ArrowDown"
            ? (current + 1) % filtered.length
            : (current - 1 + filtered.length) % filtered.length,
        );
      }
      if (event.key === "Enter") {
        event.preventDefault();
        setOpen(false);
        filtered[selectedIndex]?.action();
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [filtered, open, selectedIndex]);

  if (!open) return null;

  return (
    <div
      className="command-palette-backdrop"
      onMouseDown={() => setOpen(false)}
    >
      <section
        aria-label="Command palette"
        aria-modal="true"
        className="command-palette"
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
      >
        <TextField
          autoFocus
          className="command-palette-search"
          onChange={(event) => {
            setQuery(event.target.value);
            setSelectedIndex(0);
          }}
          placeholder="Search commands..."
          value={query}
        />
        <div className="command-palette-list">
          {filtered.length ? (
            filtered.map((command, index) => (
              <button
                aria-selected={index === selectedIndex}
                className={`command-palette-command ${index === selectedIndex ? "is-selected" : ""}`}
                key={command.label}
                onClick={() => {
                  setSelectedIndex(index);
                  setOpen(false);
                  command.action();
                }}
                type="button"
              >
                <span>{command.label}</span>
                {command.shortcut && <kbd>{command.shortcut}</kbd>}
              </button>
            ))
          ) : (
            <p className="command-palette-empty">No commands found.</p>
          )}
        </div>
        <p className="command-palette-hint">Press Esc to close</p>
      </section>
    </div>
  );
}
